import { useState, useRef, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { ShoppingCart, RotateCcw, BookOpen, Eye } from "lucide-react";

const BOOK_IMAGE = "https://customer-assets.emergentagent.com/job_goofy-hypatia-3/artifacts/yy862k4g_Distant%20Lighthouse.png";
// Main hero video - plays unobstructed initially and loops as background
const VIDEO_HERO = "https://customer-assets.emergentagent.com/job_goofy-hypatia-3/artifacts/sl42ejxr_TDL%20Background.mp4";
// Promotional book image for the text card
const PROMO_IMAGE = "https://customer-assets.emergentagent.com/job_goofy-hypatia-3/artifacts/3qzke4vx_Post%201.png";
const INGRAM_LINK = "https://shop.ingramspark.com/b/084?params=WOjJaIkWSWaOGLiXcoDCT2QxXTvD81n1g0bmkVhVhbs";
const AMAZON_LOGO = "https://upload.wikimedia.org/wikipedia/commons/a/a9/Amazon_logo.svg";
// Golden open book for Discover button background
const GOLDEN_BOOK = "https://customer-assets-39nsmqrw.emergentagent.net/job_goofy-hypatia-3/artifacts/s3vkyhf2_omlight-open-golden-book-smooth-transparent.png";

export const HeroSection = () => {
  const [phase, setPhase] = useState("intro"); // "intro" | "assembling" | "content"
  const [hoveredCard, setHoveredCard] = useState(null); // null | "text" | "book"
  const [showFragments, setShowFragments] = useState(false);
  const introVideoRef = useRef(null);
  const bgVideoRef = useRef(null);
  const discoverButtonRef = useRef(null);
  const targetButtonRef = useRef(null);

  // Dispatch custom event when video plays to trigger music
  const triggerMusicPlay = useCallback(() => {
    window.dispatchEvent(new CustomEvent('heroVideoPlaying'));
  }, []);

  useEffect(() => {
    const video = introVideoRef.current;
    if (!video || phase !== "intro") return;

    // When video starts playing, trigger music
    const handleVideoPlay = () => {
      triggerMusicPlay();
    };

    const handleVideoEnd = () => {
      // Transition to content phase after intro video ends
      setTimeout(() => {
        setPhase("content");
      }, 300);
    };

    video.addEventListener("play", handleVideoPlay);
    video.addEventListener("playing", handleVideoPlay);
    video.addEventListener("ended", handleVideoEnd);
    
    return () => {
      video.removeEventListener("play", handleVideoPlay);
      video.removeEventListener("playing", handleVideoPlay);
      video.removeEventListener("ended", handleVideoEnd);
    };
  }, [phase, triggerMusicPlay]);

  const handleReplay = () => {
    // Reset everything and replay from beginning
    setPhase("intro");
    setHoveredCard(null);
    if (introVideoRef.current) {
      introVideoRef.current.currentTime = 0;
      introVideoRef.current.play();
    }
  };

  const handleDiscoverClick = () => {
    // Start the assembling animation - button shatters and pieces fly to form content
    setShowFragments(true);
    setPhase("assembling");
    
    // After fragments assemble, show content
    setTimeout(() => {
      setPhase("content");
      setShowFragments(false);
    }, 1200);
  };

  // Generate fragment positions for the shatter/assemble effect
  const generateFragments = () => {
    const fragments = [];
    const rows = 4;
    const cols = 8;
    
    for (let i = 0; i < rows * cols; i++) {
      const row = Math.floor(i / cols);
      const col = i % cols;
      
      // Calculate initial position (from button shape)
      const startX = (col - cols / 2) * 25;
      const startY = (row - rows / 2) * 15;
      
      // Calculate explosion trajectory (outward burst)
      const angle = Math.atan2(startY, startX);
      const distance = 80 + Math.random() * 120;
      const midX = Math.cos(angle) * distance;
      const midY = Math.sin(angle) * distance;
      
      // Final position (converge to center forming the cards)
      const finalX = (col < cols / 2 ? -1 : 1) * (150 + Math.random() * 100);
      const finalY = (row < rows / 2 ? -1 : 1) * (80 + Math.random() * 60);
      
      fragments.push({
        id: i,
        startX, startY,
        midX, midY,
        finalX, finalY,
        width: 20 + Math.random() * 30,
        height: 12 + Math.random() * 20,
        rotation: Math.random() * 360,
        delay: Math.random() * 150,
        isGold: Math.random() > 0.4,
      });
    }
    return fragments;
  };

  const fragments = generateFragments();

  return (
    <section
      data-testid="hero-section"
      className="relative min-h-screen overflow-hidden"
    >
      {/* INTRO PHASE: Hero video plays unobstructed */}
      {phase === "intro" && (
        <div className="absolute inset-0 z-10">
          <video
            ref={introVideoRef}
            autoPlay
            muted
            playsInline
            className="w-full h-full object-cover"
            data-testid="hero-intro-video"
          >
            <source src={VIDEO_HERO} type="video/mp4" />
          </video>
        </div>
      )}

      {/* CONTENT PHASE: Same video loops at 30% opacity */}
      {phase === "content" && (
        <div className="absolute inset-0 z-0">
          <video
            ref={bgVideoRef}
            autoPlay
            loop
            muted
            playsInline
            className="w-full h-full object-cover opacity-30"
            data-testid="hero-bg-video"
          >
            <source src={VIDEO_HERO} type="video/mp4" />
          </video>
        </div>
      )}

      {/* Discover button - visible during intro, morphs on click */}
      <div
        className={`absolute inset-0 z-20 flex flex-col items-center justify-end pb-36 sm:pb-24 transition-opacity duration-300 ${
          phase === "intro" ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
      >
        <button
          ref={discoverButtonRef}
          data-testid="hero-discover-button-initial"
          onClick={handleDiscoverClick}
          className="relative overflow-visible group"
          style={{
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
          }}
        >
          {/* Golden book background image */}
          <div className="relative">
            <img 
              src={GOLDEN_BOOK}
              alt=""
              className="w-64 sm:w-80 h-auto"
              style={{
                filter: 'drop-shadow(0 10px 30px rgba(212,175,55,0.4))',
              }}
            />
            {/* Text overlay on the book */}
            <div 
              className="absolute inset-0 flex items-center justify-center"
              style={{
                paddingBottom: '10%', // Adjust text position on book
              }}
            >
              <span 
                className="font-serif text-lg sm:text-xl text-[#2D3E4A] tracking-wide group-hover:scale-105 transition-transform duration-300"
                style={{
                  textShadow: '0 1px 2px rgba(255,255,255,0.5)',
                }}
              >
                Discover the Story
              </span>
            </div>
            {/* Shimmer/shine effect overlay */}
            <div 
              className="absolute inset-0 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-500"
              style={{
                background: 'linear-gradient(105deg, transparent 20%, rgba(255,255,255,0.3) 50%, transparent 80%)',
                animation: 'shimmerMove 2s ease-in-out infinite',
              }}
            />
          </div>
        </button>
      </div>

      {/* ASSEMBLING ANIMATION - Button shatters into pieces that reform into content cards */}
      {showFragments && (
        <div className="fixed inset-0 z-30 pointer-events-none overflow-hidden">
          {/* Shatter fragments from button */}
          {fragments.map((frag) => (
            <div
              key={frag.id}
              className={`absolute rounded-sm ${frag.isGold ? 'bg-gradient-to-br from-[#D4AF37] to-[#E8C976]' : 'bg-gradient-to-br from-[#3A5A6A] to-[#4A6A7A]'}`}
              style={{
                width: frag.width,
                height: frag.height,
                left: '50%',
                top: '60%',
                boxShadow: frag.isGold 
                  ? '0 2px 8px rgba(212,175,55,0.5)' 
                  : '0 2px 8px rgba(58,90,106,0.5)',
                animation: `shatterAssemble 1100ms cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards`,
                animationDelay: `${frag.delay}ms`,
                '--start-x': `${frag.startX}px`,
                '--start-y': `${frag.startY}px`,
                '--mid-x': `${frag.midX}px`,
                '--mid-y': `${frag.midY}px`,
                '--final-x': `${frag.finalX}px`,
                '--final-y': `${frag.finalY}px`,
                '--rotation': `${frag.rotation}deg`,
              }}
            />
          ))}
          
          {/* Golden light burst from center */}
          <div 
            className="absolute left-1/2 top-[60%] -translate-x-1/2 -translate-y-1/2"
            style={{
              animation: 'lightBurst 800ms ease-out forwards',
            }}
          >
            <div className="w-4 h-4 rounded-full bg-[#D4AF37]" style={{ boxShadow: '0 0 60px 30px rgba(212,175,55,0.6)' }} />
          </div>
          
          {/* Sparkle particles */}
          {[...Array(20)].map((_, i) => {
            const angle = (i / 20) * Math.PI * 2;
            const distance = 100 + Math.random() * 150;
            return (
              <div
                key={`sparkle-${i}`}
                className="absolute w-1 h-1 bg-[#D4AF37] rounded-full"
                style={{
                  left: '50%',
                  top: '60%',
                  animation: `sparkle 800ms ease-out forwards`,
                  animationDelay: `${100 + i * 20}ms`,
                  '--angle': `${angle}rad`,
                  '--distance': `${distance}px`,
                }}
              />
            );
          })}
          
          {/* Convergence rings */}
          <div 
            className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[400px] border-2 border-[#D4AF37]/30 rounded-3xl"
            style={{
              animation: 'ringConverge 1000ms ease-out forwards',
              animationDelay: '200ms',
            }}
          />
          <div 
            className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[500px] border border-[#D4AF37]/20 rounded-3xl"
            style={{
              animation: 'ringConverge 1000ms ease-out forwards',
              animationDelay: '100ms',
            }}
          />
        </div>
      )}

      {/* CONTENT STATE: Slides in after assembling */}
      <div
        className={`absolute inset-0 z-20 flex items-start lg:items-center pt-20 lg:pt-0 overflow-y-auto transition-all duration-700 ${
          phase === "content" || phase === "assembling" ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
        style={{
          transform: phase === "content" ? 'scale(1)' : 'scale(0.95)',
          transition: 'transform 700ms cubic-bezier(0.34, 1.56, 0.64, 1), opacity 500ms ease-out',
        }}
      >
        {/* Dim overlay when hovering a card - only on desktop */}
        <div
          className={`hidden lg:block absolute inset-0 bg-black transition-opacity duration-300 pointer-events-none ${
            hoveredCard ? "opacity-50" : "opacity-0"
          }`}
        />

        <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-4 sm:py-8 relative z-10">
          {/* Mobile: Stack vertically with book first, Desktop: Side by side */}
          <div className="flex flex-col lg:grid lg:grid-cols-2 gap-4 sm:gap-6 lg:gap-12 items-stretch lg:items-center">
            
            {/* MOBILE ORDER: Book card first (order-1), Text card second (order-2) */}
            {/* DESKTOP ORDER: Text card first (lg:order-1), Book card second (lg:order-2) */}
            
            {/* Text Card with Promo Image - appears second on mobile, first on desktop */}
            <div
              className={`order-2 lg:order-1 transform transition-all duration-700 ${
                phase === "content" ? "translate-y-0 lg:translate-x-0 opacity-100" : "translate-y-10 lg:-translate-x-full opacity-0"
              } ${hoveredCard === "text" ? "lg:scale-105 lg:z-30" : ""} ${
                hoveredCard === "book" ? "lg:opacity-40" : ""
              }`}
              style={{ transitionDelay: phase === "content" ? "0.3s" : "0s" }}
              onMouseEnter={() => setHoveredCard("text")}
              onMouseLeave={() => setHoveredCard(null)}
            >
              {/* 3D Card with depth layers */}
              <div className="relative">
                {/* Back shadow layer for 3D depth */}
                <div className="absolute inset-0 bg-black/30 rounded-xl sm:rounded-2xl transform translate-x-2 translate-y-2 blur-sm" />
                <div className="absolute inset-0 bg-[#2A4A5A]/60 rounded-xl sm:rounded-2xl transform translate-x-1 translate-y-1" />
                
                {/* Main card */}
                <div 
                  className={`relative bg-gradient-to-br from-[#3A5A6A]/95 via-[#3A5A6A]/90 to-[#2A4A5A]/95 backdrop-blur-md rounded-xl sm:rounded-2xl p-4 sm:p-6 lg:p-8 shadow-2xl border border-white/20 cursor-pointer transition-all duration-300 ${
                    hoveredCard === "text" ? "lg:shadow-[0_0_60px_rgba(212,175,55,0.3)] lg:-translate-y-1" : ""
                  }`}
                  data-testid="hero-text-card"
                  style={{
                    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5), inset 0 1px 0 rgba(255,255,255,0.1)',
                  }}
                >
                  {/* Layout: Side by side on all screens */}
                  <div className="flex flex-row gap-3 sm:gap-4 lg:gap-6 items-start">
                    {/* Text Content */}
                    <div className="flex-1 min-w-0">
                      <p className="text-[9px] sm:text-xs tracking-[0.1em] sm:tracking-[0.2em] uppercase font-bold text-[#D4AF37] font-sans mb-1 sm:mb-3">
                        Omlight Publications
                      </p>

                      <h1 className="font-serif text-base sm:text-2xl lg:text-4xl xl:text-5xl tracking-tight leading-tight text-white mb-1 sm:mb-3">
                        The <em className="italic text-[#F8C471]">Distant</em>{" "}
                        <span className="block sm:inline">Lighthouse</span>
                      </h1>

                      <p className="font-serif text-[10px] sm:text-base lg:text-lg text-white/90 mb-1 sm:mb-3">
                        by Linda D. Gallagher
                      </p>

                      {/* Message - visible on ALL screens */}
                      <div className="font-sans text-[9px] sm:text-sm text-white/80 leading-relaxed mb-2 sm:mb-4">
                        <p className="mb-0.5 sm:mb-1">A story about <strong className="text-white">forgetting</strong> —</p>
                        <p className="mb-0.5 sm:mb-1">and then, remembering.</p>
                        <p className="text-[#F8C471] font-medium">And how sweet that is.</p>
                      </div>

                      <div className="flex justify-start">
                        <a href="#book" ref={targetButtonRef}>
                          <Button
                            data-testid="hero-discover-story-button"
                            className="relative overflow-hidden bg-gradient-to-r from-[#D4AF37] via-[#E8C976] to-[#D4AF37] text-[#1A202C] hover:from-[#B3932E] hover:via-[#D4AF37] hover:to-[#B3932E] px-3 sm:px-6 py-2 sm:py-3 font-serif text-[10px] sm:text-sm border border-[#B3932E]/50 shadow-[0_4px_15px_rgba(212,175,55,0.3)] hover:shadow-[0_6px_20px_rgba(212,175,55,0.5)] hover:-translate-y-0.5 transition-all duration-300 group"
                          >
                            <span className="relative flex items-center gap-1 sm:gap-2">
                              Discover the Story
                              <span className="group-hover:translate-x-1 transition-transform duration-300">→</span>
                            </span>
                          </Button>
                        </a>
                      </div>
                    </div>
                    
                    {/* Promo Image inside text card - always visible */}
                    <div className="w-20 sm:w-32 lg:w-48 xl:w-56 flex-shrink-0">
                      <img
                        src={PROMO_IMAGE}
                        alt="The Distant Lighthouse promotional image"
                        data-testid="hero-promo-image"
                        className="w-full h-auto rounded-lg shadow-lg"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Book Card with Purchase Options - appears first on mobile, second on desktop */}
            <div
              className={`order-1 lg:order-2 transform transition-all duration-700 ${
                phase === "content" ? "translate-y-0 lg:translate-x-0 opacity-100" : "-translate-y-10 lg:translate-x-full opacity-0"
              } ${hoveredCard === "book" ? "lg:scale-105 lg:z-30" : ""} ${
                hoveredCard === "text" ? "lg:opacity-40" : ""
              }`}
              style={{ transitionDelay: phase === "content" ? "0.1s" : "0s" }}
              onMouseEnter={() => setHoveredCard("book")}
              onMouseLeave={() => setHoveredCard(null)}
            >
              {/* 3D Card with depth layers */}
              <div className="relative">
                {/* Back shadow layer for 3D depth */}
                <div className="absolute inset-0 bg-black/30 rounded-xl sm:rounded-2xl transform translate-x-2 translate-y-2 blur-sm" />
                <div className="absolute inset-0 bg-[#2A4A5A]/60 rounded-xl sm:rounded-2xl transform translate-x-1 translate-y-1" />
                
                {/* Main card */}
                <div 
                  className={`relative bg-gradient-to-br from-[#3A5A6A]/95 via-[#3A5A6A]/90 to-[#2A4A5A]/95 backdrop-blur-md rounded-xl sm:rounded-2xl p-4 sm:p-6 lg:p-8 shadow-2xl border border-white/20 cursor-pointer transition-all duration-300 ${
                    hoveredCard === "book" ? "lg:shadow-[0_0_60px_rgba(212,175,55,0.3)] lg:-translate-y-1" : ""
                  }`}
                  data-testid="hero-book-card"
                  style={{
                    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5), inset 0 1px 0 rgba(255,255,255,0.1)',
                  }}
                >
                  <div className="flex flex-col items-center">
                    <img
                      src={BOOK_IMAGE}
                      alt="The Distant Lighthouse book cover"
                      data-testid="hero-book-image"
                      className="w-32 sm:w-44 md:w-48 lg:w-56 xl:w-64 h-auto mb-4 sm:mb-6 drop-shadow-2xl"
                      style={{ 
                        filter: 'drop-shadow(0 25px 25px rgba(0,0,0,0.25))',
                      }}
                    />

                    {/* Purchase Button */}
                    <a
                      href={INGRAM_LINK}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="book-card-purchase-button"
                      className="w-full max-w-xs"
                    >
                      <Button className="btn-gold font-sans text-xs sm:text-sm w-full shadow-lg group py-3 sm:py-5">
                        <ShoppingCart className="w-3 h-3 sm:w-4 sm:h-4 mr-2 group-hover:scale-110 transition-transform" />
                        Purchase Book
                      </Button>
                    </a>
                    <p className="font-sans text-[10px] sm:text-xs text-white/70 mt-2 text-center">
                      Special discounted rate via IngramSpark
                    </p>

                    {/* Read Sample Button - Direct open on mobile, scroll on desktop */}
                    <button
                      onClick={() => {
                        // On mobile, dispatch event to open sample modal directly
                        if (window.innerWidth < 1024) {
                          window.dispatchEvent(new CustomEvent('openBookSample'));
                        } else {
                          // On desktop, scroll to book section
                          document.getElementById('book')?.scrollIntoView({ behavior: 'smooth' });
                        }
                      }}
                      data-testid="hero-read-sample-button"
                      className="w-full max-w-xs mt-3"
                    >
                      <Button 
                        variant="outline" 
                        className="bg-white/10 border-white/40 text-white hover:bg-white/20 hover:text-white font-sans text-xs sm:text-sm w-full py-3 sm:py-5 group"
                      >
                        <Eye className="w-3 h-3 sm:w-4 sm:h-4 mr-2 group-hover:scale-110 transition-transform" />
                        Read a Sample
                      </Button>
                    </button>

                    {/* Amazon Section */}
                    <div className="mt-4 sm:mt-6 pt-3 sm:pt-4 border-t border-white/20 w-full text-center">
                      <p className="font-sans text-[10px] sm:text-xs text-white/60 mb-2">Also Available @</p>
                      <a
                        href="https://www.amazon.com/Distant-Lighthouse-Linda-D-Gallagher/dp/1969367814"
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="amazon-link"
                        className="inline-block"
                      >
                        <img
                          src={AMAZON_LOGO}
                          alt="Amazon"
                          className="h-5 sm:h-6 brightness-0 invert opacity-80 hover:opacity-100 transition-opacity mx-auto"
                        />
                      </a>
                    </div>
                    
                    {/* eBook Option */}
                    <div className="mt-4 pt-3 border-t border-white/20 w-full text-center">
                      <p className="font-sans text-[10px] sm:text-xs text-white/60 mb-2">
                        Prefer to read eBook?
                      </p>
                      <a
                        href="https://www.amazon.com/Distant-Lighthouse-Linda-D-Gallagher-ebook/dp/B0GJ87Q8NW/ref=tmm_kin_swatch_0"
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="kindle-link"
                      >
                        <Button 
                          variant="outline" 
                          className="bg-white/10 border-white/30 text-white hover:bg-white/20 hover:text-white font-sans text-xs px-4 py-2 h-auto"
                        >
                          <BookOpen className="w-4 h-4 mr-2" />
                          Order Kindle eBook
                        </Button>
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Replay Video Button - only visible in content phase */}
          <div className="flex justify-center mt-6 sm:mt-8 lg:mt-12">
            <button
              onClick={handleReplay}
              data-testid="replay-video-button"
              className="flex flex-col items-center gap-1 sm:gap-2 text-white/60 hover:text-white transition-colors group"
            >
              <div className="w-10 h-10 sm:w-14 sm:h-14 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 flex items-center justify-center group-hover:bg-white/20 group-hover:border-white/40 transition-all">
                <RotateCcw className="w-4 h-4 sm:w-6 sm:h-6" />
              </div>
              <span className="font-sans text-[10px] sm:text-xs tracking-wider uppercase">Replay Video</span>
            </button>
          </div>
        </div>
      </div>

      {/* CSS Animations for assembling effect */}
      <style>{`
        @keyframes shatterAssemble {
          0% {
            transform: translate(-50%, -50%) translate(var(--start-x), var(--start-y)) rotate(0deg) scale(1);
            opacity: 1;
          }
          30% {
            transform: translate(-50%, -50%) translate(var(--mid-x), var(--mid-y)) rotate(var(--rotation)) scale(1.2);
            opacity: 1;
          }
          70% {
            transform: translate(-50%, -50%) translate(calc(var(--final-x) * 0.5), calc(var(--final-y) * 0.5)) rotate(calc(var(--rotation) * 0.3)) scale(0.8);
            opacity: 0.8;
          }
          100% {
            transform: translate(-50%, -50%) translate(var(--final-x), var(--final-y)) rotate(0deg) scale(0);
            opacity: 0;
          }
        }
        
        @keyframes lightBurst {
          0% {
            transform: translate(-50%, -50%) scale(1);
            opacity: 1;
          }
          50% {
            transform: translate(-50%, -50%) scale(3);
            opacity: 0.8;
          }
          100% {
            transform: translate(-50%, -50%) scale(8);
            opacity: 0;
          }
        }
        
        @keyframes sparkle {
          0% {
            transform: translate(-50%, -50%) translate(0, 0) scale(1);
            opacity: 1;
          }
          100% {
            transform: translate(-50%, -50%) translate(calc(cos(var(--angle)) * var(--distance)), calc(sin(var(--angle)) * var(--distance))) scale(0);
            opacity: 0;
          }
        }
        
        @keyframes ringConverge {
          0% {
            transform: translate(-50%, -50%) scale(1.5);
            opacity: 0;
          }
          50% {
            opacity: 0.6;
          }
          100% {
            transform: translate(-50%, -50%) scale(0.8);
            opacity: 0;
          }
        }
        
        @keyframes shimmerMove {
          0% {
            transform: translateX(-100%);
          }
          100% {
            transform: translateX(100%);
          }
        }
      `}</style>
    </section>
  );
};

export default HeroSection;
